import { create } from 'zustand';
import * as payrollService from '../services/payrollService';

const usePayrollStore = create((set, get) => ({
  // State
  payrollRuns: [],
  currentPayrollRun: null,
  payslips: [],
  currentPayslip: null,
  payrollSummary: null,
  payrollSettings: null,
  isLoading: false,
  error: null,
  meta: null,
  filters: {
    status: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    department: '',
    employee: ''
  },
  
  // Actions
  
  // Payroll Runs
  fetchPayrollRuns: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getPayrollRuns(params);
      set({ 
        payrollRuns: response.data?.payrollRuns || [],
        meta: response.meta || null,
        isLoading: false 
      });
      return response;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch payroll runs',
        payrollRuns: [],
        isLoading: false 
      });
      // Don't throw the error - return empty data
      return { data: { payrollRuns: [] } };
    }
  },
  
  fetchPayrollRun: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getPayrollRun(id);
      set({ 
        currentPayrollRun: response.data?.payroll || response.data,
        isLoading: false 
      });
      return response.data?.payroll || response.data;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch payroll run',
        isLoading: false 
      });
      throw error;
    }
  },
  
  createPayrollRun: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.createPayrollRun(data);
      const payroll = response.data?.payroll || response.data;
      set(state => ({
        payrollRuns: [payroll, ...state.payrollRuns],
        currentPayrollRun: payroll,
        isLoading: false
      }));
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to create payroll run',
        isLoading: false 
      });
      throw error;
    }
  },
  
  updatePayrollRun: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.updatePayrollRun(id, data);
      const payroll = response.data?.payroll || response.data;
      set(state => ({
        payrollRuns: state.payrollRuns.map(run => 
          run._id === id ? payroll : run
        ),
        currentPayrollRun: payroll,
        isLoading: false
      }));
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to update payroll run',
        isLoading: false 
      });
      throw error;
    }
  },
  
  processPayroll: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.processPayroll(id);
      const payroll = response.data?.payroll || response.data;
      set(state => ({
        payrollRuns: state.payrollRuns.map(run => 
          run._id === id ? payroll : run
        ),
        currentPayrollRun: payroll,
        isLoading: false
      }));
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to process payroll',
        isLoading: false 
      });
      throw error;
    }
  },
  
  approvePayroll: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.approvePayroll(id);
      const payroll = response.data?.payroll || response.data;
      set(state => ({
        payrollRuns: state.payrollRuns.map(run => 
          run._id === id ? payroll : run
        ),
        currentPayrollRun: payroll,
        isLoading: false
      }));
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to approve payroll',
        isLoading: false 
      });
      throw error;
    }
  },
  
  finalizePayroll: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.finalizePayroll(id);
      const payroll = response.data?.payroll || response.data;
      set(state => ({
        payrollRuns: state.payrollRuns.map(run => 
          run._id === id ? payroll : run
        ),
        currentPayrollRun: payroll,
        isLoading: false
      }));
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to finalize payroll',
        isLoading: false 
      });
      throw error;
    }
  },
  
  // Payslips
  fetchPayslips: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getPayslips(params);
      set({ 
        payslips: response.data?.payslips || [],
        meta: response.meta || null,
        isLoading: false 
      });
      return response;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch payslips',
        payslips: [],
        isLoading: false 
      });
      // Don't throw the error - return empty data
      return { data: { payslips: [] } };
    }
  },
  
  fetchPayslip: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getPayslip(id);
      set({ 
        currentPayslip: response.data?.payslip || response.data,
        isLoading: false 
      });
      return response.data?.payslip || response.data;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch payslip',
        isLoading: false 
      });
      throw error;
    }
  },
  
  downloadPayslip: async (id) => {
    try {
      const response = await payrollService.downloadPayslip(id);
      return response;
    } catch (error) {
      set({ error: error.message || 'Failed to download payslip' });
      throw error;
    }
  },
  
  // Employee Payslips
  fetchEmployeePayslips: async (employeeId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getEmployeePayslips(employeeId, params);
      set({ 
        payslips: response.data?.payslips || [],
        meta: response.meta || null,
        isLoading: false 
      });
      return response;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch employee payslips',
        isLoading: false 
      });
      throw error;
    }
  },
  
  // Payroll Summary
  fetchPayrollSummary: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getPayrollSummary(params);
      set({ 
        payrollSummary: response.data?.summary || response.data,
        isLoading: false 
      });
      return response.data?.summary || response.data;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch payroll summary',
        payrollSummary: null,
        isLoading: false 
      });
      // Don't throw the error - return null
      return null;
    }
  },
  
  // Adjustments
  addPayrollAdjustment: async (payrollRunId, employeeId, adjustment) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.addPayrollAdjustment(payrollRunId, employeeId, adjustment);
      const payroll = response.data?.payroll || response.data;
      // Update current payroll run with new adjustment
      if (get().currentPayrollRun?._id === payrollRunId) {
        set(state => ({
          currentPayrollRun: payroll,
          isLoading: false
        }));
      }
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to add adjustment',
        isLoading: false 
      });
      throw error;
    }
  },
  
  removePayrollAdjustment: async (payrollRunId, employeeId, adjustmentId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.removePayrollAdjustment(payrollRunId, employeeId, adjustmentId);
      const payroll = response.data?.payroll || response.data;
      // Update current payroll run
      if (get().currentPayrollRun?._id === payrollRunId) {
        set(state => ({
          currentPayrollRun: payroll,
          isLoading: false
        }));
      }
      return payroll;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to remove adjustment',
        isLoading: false 
      });
      throw error;
    }
  },
  
  // Payroll Settings
  fetchPayrollSettings: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.getPayrollSettings();
      set({ 
        payrollSettings: response.data?.settings || null,
        isLoading: false 
      });
      return response.data?.settings;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch payroll settings',
        isLoading: false 
      });
      throw error;
    }
  },
  
  updatePayrollSettings: async (settings) => {
    set({ isLoading: true, error: null });
    try {
      const response = await payrollService.updatePayrollSettings(settings);
      set({ 
        payrollSettings: response.data?.settings || settings,
        isLoading: false 
      });
      return response.data?.settings || settings;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to update payroll settings',
        isLoading: false 
      });
      throw error;
    }
  },
  
  // Filters
  setFilters: (filters) => {
    set(state => ({
      filters: { ...state.filters, ...filters }
    }));
  },
  
  resetFilters: () => {
    set({
      filters: {
        status: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        department: '',
        employee: ''
      }
    });
  },
  
  // Clear store
  clearPayrollData: () => {
    set({
      payrollRuns: [],
      currentPayrollRun: null,
      payslips: [],
      currentPayslip: null,
      payrollSummary: null,
      payrollSettings: null,
      isLoading: false,
      error: null,
      meta: null
    });
  }
}));

export default usePayrollStore;